<script setup lang="ts">
const route = useRoute();
const router = useRouter();
const sortColumn = ref<string | null>(null);
const sortDirection = ref<string | null>(null);
const { hideRecommendedPrice } = useRuntimeConfig().public;

const props = defineProps({
  isLoading: {
    type: Boolean,
    required: true,
  },
  showListed: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const updateSortParamsFromRoute = () => {
  if (route.query.filter) {
    const filterParams = decodeURIComponent(route.query.filter.toString());
    const params = new URLSearchParams(filterParams);
    if (params.has("sort")) sortColumn.value = params.get("sort");
    if (params.has("order")) sortDirection.value = params.get("order");
  } else {
    sortColumn.value = props.showListed ? "PRICE" : "RECOMMENDED_PRICE";
    sortDirection.value = "ASC";
  }
};

const handleSort = (column: string) => {
  if (props.isLoading) return;
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
  } else {
    sortColumn.value = column;
    sortDirection.value = "DESC";
  }
  const updatedQuery = { ...route.query };
  delete updatedQuery.filter;
  const filterParams = `&sort=${encodeURIComponent(
    sortColumn.value
  )}&order=${encodeURIComponent(sortDirection.value)}`;
  router.push({
    query: {
      ...updatedQuery,
      filter: filterParams,
    },
  });
};

watch(
  () => route.query,
  async () => {
    updateSortParamsFromRoute();
  }
);

updateSortParamsFromRoute();

const { getMerchantAttributes } = useLuxuryDesign();
const headerAttributes = computed(() =>
  getMerchantAttributes((route.params.merchant as string) || "")
);
</script>

<template>
  <div
    v-bind="headerAttributes"
    class="hidden sm:grid grid-cols-4 sm:grid-cols-12 w-full mb-4">
    <span class="text-secondary-200 font-semibold text-xs text-center">
      {{ $t("main.cardCollectible.visual") }}
    </span>
    <span
      :class="{
        'text-primary-500': sortColumn === 'LEVEL',
        'text-secondary-200': sortColumn !== 'LEVEL',
      }"
      class="font-semibold text-xs text-center cursor-pointer collectible-stack-list-header__level"
      data-test="button-sort-stack-level"
      @click="handleSort('LEVEL')">
      <span class="flex items-center justify-center">
        {{ $t("main.cardCollectible.level") }}
        <NuxtIcon
          v-if="sortColumn === 'LEVEL'"
          name="mdi:arrow-down"
          :class="{
            'rotate-180': sortDirection === 'ASC',
          }"
          class="h-[0.8rem]"
          color="fill-primary-500" />
      </span>
    </span>
    <span
      :class="{
        'text-primary-500': sortColumn === 'NUMBER',
        'text-secondary-200': sortColumn !== 'NUMBER',
      }"
      class="font-semibold text-xs text-center col-span-1 sm:hidden xl:block xl:col-span-1 cursor-pointer"
      data-test="button-sort-stack-number"
      @click="handleSort('NUMBER')">
      <span class="flex items-center justify-center">
        {{ $t("main.cardCollectible.number") }}
        <NuxtIcon
          v-if="sortColumn === 'NUMBER'"
          name="mdi:arrow-down"
          :class="{
            'rotate-180': sortDirection === 'ASC',
          }"
          class="h-[0.8rem]"
          color="fill-primary-500" />
      </span>
    </span>
    <span
      class="text-secondary-200 font-semibold text-xs text-center sm:hidden xl:block col-span-3"
      :class="
        showListed || hideRecommendedPrice.toString() !== 'true'
          ? 'sm:col-span-3'
          : 'sm:col-span-6'
      ">
      {{ $t("main.cardCollectible.owner") }}
    </span>
    <span
      v-if="showListed || hideRecommendedPrice.toString() !== 'true'"
      :class="{
        'text-primary-500':
          sortColumn === 'PRICE' || sortColumn === 'RECOMMENDED_PRICE',
        'text-secondary-200':
          sortColumn !== 'PRICE' && sortColumn !== 'RECOMMENDED_PRICE',
      }"
      class="font-semibold text-xs text-center sm:col-span-4 xl:col-span-3 cursor-pointer"
      data-test="button-sort-stack-price"
      @click="handleSort(showListed ? 'PRICE' : 'RECOMMENDED_PRICE')">
      <span class="flex items-center justify-center">
        {{
          showListed
            ? $t("main.cardCollectible.price")
            : $t("main.cardCollectible.recommendedPrice")
        }}
        <NuxtIcon
          v-if="sortColumn === 'PRICE' || sortColumn === 'RECOMMENDED_PRICE'"
          name="mdi:arrow-down"
          :class="{
            'rotate-180': sortDirection === 'ASC',
          }"
          class="h-[0.8rem]"
          color="fill-primary-500" />
      </span>
    </span>
    <span
      class="text-secondary-200 font-semibold text-xs text-center sm:col-span-6 xl:col-span-3">
      {{ $t("main.cardCollectible.quickActions") }}
    </span>
  </div>
</template>
